import { zodResolver } from "@hookform/resolvers/zod";
import {
  Box,
  HStack,
  KdsButtonIcon,
  KdsDivider,
  KdsDrawer,
  KdsFocusableElement,
  KdsSpinner,
  KdsText,
  KdsTooltip,
  VStack,
} from "@kazaar-platform/design-system";
import { RiInformationLine, RiMessage2Line } from "@remixicon/react";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslate } from "@tolgee/react";
import { useEffect, useRef, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useParams } from "react-router-dom";
import { UserEntityPreferredLanguage } from "@/api/__generated__/orval/auth";
import { ControlledCheckbox, ControlledTextField } from "@/components/Form";
import {
  OrderItemDetailsOrderLogReadDtoActionTypeEnum,
  PsProductFieldTypeEnum,
} from "@/config/apiClient";
import { useExpertMode, useOrderItems, useToasts } from "@/hooks";
import { ModalConfirmProductChange } from "@/modals/expertMode";
import { tolgee } from "@/providers/TranslationProvider";
import { CommentsList } from "../Comments";
import { ExpressionOfNeed } from "../ExpressionOfNeed";
import { QualifyTechnicalFieldsForm } from "./_partials";
import {
  type QualifyDrawerFormType,
  qualifyDrawerFormSchema,
} from "./QualifyDrawerForm.schema";
import { type QualifyDrawerProps } from "./QualifyDrawerForm.types";

const QualifyDrawerForm = ({
  orderId,
  orderItemId,
  isOpen,
  handleOpenChange,
}: QualifyDrawerProps) => {
  const { t } = useTranslate();
  const { orderItemId: pathOrderItemId, orderId: pathOrderId } = useParams();
  const toaster = useToasts();
  const queryClient = useQueryClient();

  const commentAreaRef = useRef<HTMLDivElement>(null);
  const previousProductIdRef = useRef<number | undefined>(undefined);
  const [pendingProductId, setPendingProductId] = useState<number | null>(null);
  const [comboboxKey, setComboboxKey] = useState(0);

  const { useGetOrderItem, useUpdateOrderItem } = useOrderItems();

  const { data: orderItemData, isLoading } = useGetOrderItem({
    orderId: orderId || pathOrderId || "",
    orderItemId: orderItemId || pathOrderItemId || "",
  });
  const orderItem = orderItemData?.data.orderItem;
  const commentableOrderLog = orderItem?.OrderLog.find(
    (e) =>
      e.actionType ===
      OrderItemDetailsOrderLogReadDtoActionTypeEnum.ItemSentToStudy
  );

  const { useQualifyBrief, useGetTechnicalFields, useGetProductsByClient } =
    useExpertMode();
  const { mutate, isPending: isQualifyingBrief } = useQualifyBrief();
  const { mutate: updateOrderItem } = useUpdateOrderItem();

  const { data: products } = useGetProductsByClient({
    language: tolgee.getLanguage() || UserEntityPreferredLanguage.en,
  });

  const formMethods = useForm<QualifyDrawerFormType>({
    resolver: zodResolver(qualifyDrawerFormSchema),
    defaultValues: {
      productId: undefined,
      deliverableId: undefined,
      productName: "",
      fields: {},
      withPreMedia: false,
    },
  });

  const productId = formMethods.watch("productId");
  const selectedProduct = products?.data.find(
    (e) => e.product_id === productId
  );

  const hasFilledFields = () => {
    const currentFields = formMethods.getValues("fields");
    return Object.values(currentFields).some(
      (value) => value !== undefined && value !== ""
    );
  };

  const handleProductChange = (key: string | number | null) => {
    if (typeof key !== "number") {
      return;
    }

    const previousProductId = previousProductIdRef.current;
    const isChangingProduct =
      previousProductId !== undefined && previousProductId !== key;

    if (isChangingProduct && hasFilledFields()) {
      formMethods.setValue("productId", previousProductId);
      setComboboxKey((k) => k + 1);
      setPendingProductId(key);
    } else {
      previousProductIdRef.current = key;
    }
  };

  const handleConfirmProductChange = () => {
    if (pendingProductId === null) {
      return;
    }

    formMethods.setValue("fields", {});
    formMethods.resetField("deliverableId");
    formMethods.setValue("productId", pendingProductId);
    previousProductIdRef.current = pendingProductId;
    setComboboxKey((k) => k + 1);
    setPendingProductId(null);
  };

  const handleCancelProductChange = () => setPendingProductId(null);

  const { data: fields, isLoading: isLoadingFields } = useGetTechnicalFields({
    admaId: selectedProduct?.product_id || 0,
    awsVersion: selectedProduct?.technical_version || 0,
  });

  const handleSuccessSubmit = () => {
    handleOpenChange();
    queryClient.invalidateQueries({
      queryKey: [
        "orderItem",
        pathOrderId || orderId,
        pathOrderItemId || orderItemId,
      ],
    });
    queryClient.invalidateQueries({
      queryKey: ["order"],
    });

    toaster.add({
      state: "success",
      title: t("expertMode.qualifyDrawer.toasts.success.title"),
    });
  };

  const onSubmit = async (values: QualifyDrawerFormType) => {
    if (!orderItem) {
      return;
    }

    const formFields = values.fields;
    const filledFields = Object.keys(formFields)
      .filter((key) => formFields[key] !== undefined && formFields[key] !== "")
      .map((key) => {
        const originalField = fields?.data.fields.find(
          (f) => f.id === Number.parseInt(key, 10)
        );
        return {
          fieldId: Number.parseInt(key, 10),
          fieldValue: ["true", "false"].includes(formFields[key])
            ? formFields[key] === "true"
            : formFields[key],
          fieldValueId:
            originalField?.type === PsProductFieldTypeEnum.DropdownList
              ? Number.parseInt(formFields[key], 10)
              : undefined,
          type: originalField?.type || "",
          name: originalField?.name || "",
          position: originalField?.position || 0,
          category: originalField?.categorie || 0,
        };
      });

    mutate(
      {
        admaId: values.productId,
        orderItemId: orderItem?.id,
        technicalOptions: filledFields,
        technicalVersion:
          products?.data?.find((e) => e.product_id === values.productId)
            ?.technical_version || 0,
        deliverableId: values.deliverableId,
        name: values.productName,
      },
      { onSuccess: handleSuccessSubmit }
    );
  };

  useEffect(() => {
    if (!orderItem?.sandboxItem) {
      return;
    }

    const existingFields: { [key: number]: string | number } = {};
    orderItem?.sandboxItem.catalogMedium?.CatalogMediumOption.forEach(
      (option) => {
        existingFields[option.admaId] =
          PsProductFieldTypeEnum.DropdownList === option.type
            ? Number.parseInt(option.value, 10)
            : option.value;
      }
    );

    const initialProductId =
      orderItem?.sandboxItem.catalogMediumAdmaId || undefined;

    formMethods.reset({
      productId: initialProductId,
      deliverableId:
        orderItem?.sandboxItem.catalogMediumDeliverableId || undefined,
      fields: existingFields,
      productName: orderItem.sandboxItem.name,
      withPreMedia: orderItem.sandboxItem.withPreMedia || false,
    });

    previousProductIdRef.current = initialProductId;
  }, [
    orderItem?.sandboxItem.catalogMediumAdmaId,
    orderItem?.sandboxItem.catalogMedium?.CatalogMediumOption,
  ]);

  useEffect(() => {
    const newWithPreMedia = formMethods.getValues("withPreMedia");
    if (!orderItem || newWithPreMedia === orderItem.sandboxItem.withPreMedia) {
      return;
    }

    updateOrderItem({
      orderItemId: orderItem.id,
      orderId: orderItem.orderId,
      orderItemUpdateCommand: {
        withPreMedia: newWithPreMedia,
      },
    });
  }, [formMethods.watch("withPreMedia")]);

  const handleClickCommentsButton = () => {
    if (!commentAreaRef?.current) {
      return;
    }
    commentAreaRef?.current?.querySelector("textarea")?.focus();
  };

  if (isLoading) {
    return <KdsSpinner />;
  }

  return (
    <FormProvider {...formMethods}>
      <form
        id="qualificationForm"
        onSubmit={formMethods.handleSubmit(onSubmit)}
      >
        <KdsDrawer
          size="lg"
          isDismissable={false}
          isOpen={isOpen}
          onOpenChange={handleOpenChange}
          dialogProps={{
            overflow: true,
            dialogHeaderProps: {
              title: t("expertMode.qualifyDrawer.title"),
            },
            content: (
              <HStack gap="sp-md" h="100%">
                <Box w="33%">
                  <VStack gap="sp-md">
                    <ControlledTextField
                      name="productName"
                      label={t("expertMode.qualifyDrawer.productName")}
                      value={orderItem?.sandboxItem.name}
                    />

                    <KdsDivider />
                    <Box h="auto" w="100%">
                      <VStack gap="sp-md">
                        <HStack
                          w="100%"
                          justify="space-between"
                          align="center"
                          h="auto"
                        >
                          <KdsText size="title-sm">
                            {t(
                              "expertMode.qualifyDrawer.expressionOfNeed.title"
                            )}
                          </KdsText>

                          <KdsButtonIcon
                            alt={t("buttons.comment")}
                            size="small"
                            color="neutral"
                            variant="outlined"
                            icon={RiMessage2Line}
                            onPress={handleClickCommentsButton}
                          />
                        </HStack>

                        {(!!orderItem?.sandboxItem.specificRequest ||
                          !!orderItem?.sandboxItem.preMediaFiles?.length) && (
                          <ExpressionOfNeed
                            description={
                              orderItem?.sandboxItem.specificRequest || ""
                            }
                            files={orderItem?.sandboxItem.preMediaFiles}
                          />
                        )}

                        <Box
                          bgColor="primary.surface"
                          py="sp-sm"
                          px="sp-md"
                          w="100%"
                          rounded="br-sm"
                        >
                          <HStack gap="sp-xs" align="center">
                            <ControlledCheckbox name="withPreMedia" />
                            <KdsText size="subtitle-sm" color="primary-text">
                              {t(
                                "expertMode.qualifyDrawer.expressionOfNeed.withPremedia"
                              )}
                            </KdsText>
                            <KdsTooltip
                              placement="top"
                              alt={t(
                                "expertMode.qualifyDrawer.expressionOfNeed.withPremediaTooltip"
                              )}
                            >
                              <KdsFocusableElement>
                                <RiInformationLine size="16px" />
                              </KdsFocusableElement>
                            </KdsTooltip>
                          </HStack>
                        </Box>
                      </VStack>
                    </Box>
                    <KdsDivider />

                    <Box h="100%" maxH="100%" overflowY="auto" w="full">
                      <VStack gap="sp-md">
                        <KdsText size="title-sm">
                          {t("expertMode.qualifyDrawer.comments.title")}
                        </KdsText>

                        {!!orderItem && commentableOrderLog?.id && (
                          <CommentsList
                            orderItemId={orderItem.id}
                            comments={commentableOrderLog?.comments || []}
                            orderLogId={commentableOrderLog.id}
                            commentAreaRef={commentAreaRef}
                          />
                        )}
                      </VStack>
                    </Box>
                  </VStack>
                </Box>

                <Box w="67%">
                  <QualifyTechnicalFieldsForm
                    products={products?.data || []}
                    fields={fields?.data}
                    isLoadingFields={isLoadingFields}
                    onProductChange={handleProductChange}
                    productComboboxKey={comboboxKey}
                  />
                </Box>
              </HStack>
            ),
            dialogFooterProps: {
              buttonProps: [
                {
                  variant: "ghost",
                  color: "neutral",
                  children: t("buttons.cancel"),
                  onPress: () => handleOpenChange(),
                },
                {
                  children: t("buttons.save"),
                  type: "submit",
                  form: "qualificationForm",
                  isLoading: isQualifyingBrief,
                },
              ],
            },
          }}
        />
      </form>

      <ModalConfirmProductChange
        isOpen={pendingProductId !== null}
        onOpenChange={handleCancelProductChange}
        onConfirm={handleConfirmProductChange}
      />
    </FormProvider>
  );
};

export { QualifyDrawerForm };
